// EVA: a person in a suit, out in space, moving in six degrees of freedom with suit thrusters.
//
// Like a shuttle, the suit flies against space: the position and velocity are kept relative to the ship's centre in
// space axes, and every frame takes away what the ship did (its change of velocity, its turning). The suit's assist
// (on by default, T toggles it) holds you steady relative to the ship, up to what the thrusters can push; a ship that
// accelerates harder than that leaves you behind. The suit carries about twenty minutes of oxygen.
//   W/S forward and back · A/D slide · Space/C up and down · mouse turns you · Q/E roll · Shift boosts
import RAPIER from "@dimforge/rapier3d-compat";
import { Matrix, Quaternion, Vector3 } from "@babylonjs/core";
import type { Vec3 } from "../shared/ship.js";
import { add, ap, cross, len, mul, orthonormal, rot, scl, sub, toQuat, tr, type M3 } from "./shuttle.js";

const SPEED = 6, BOOST = 15;          // m/s relative to the ship
const FEEL = 3;                        // m/s/s, how hard the suit pushes for your own moves
const FOLLOW_MAX = 60;                 // m/s/s, the most the suit can do to keep up with the ship
const MANUAL = 2;                      // m/s/s of thrust with the assist off
export const O2_SECONDS = 20 * 60;

export class Eva {
  active = false; assist = true; o2 = O2_SECONDS;
  p: Vec3 = { x: 0, y: 0, z: 0 }; q = Quaternion.Identity();    // ship-frame position (body centre) and orientation
  u: Vec3 = { x: 0, y: 0, z: 0 };                                 // ship-frame velocity
  saturated = 0;
  private relP: Vec3 = { x: 0, y: 0, z: 0 }; private relV: Vec3 = { x: 0, y: 0, z: 0 }; private R: M3 = [1, 0, 0, 0, 1, 0, 0, 0, 1];
  private prevShipR: M3 = [1, 0, 0, 0, 1, 0, 0, 0, 1]; private prevShipV: Vec3 = { x: 0, y: 0, z: 0 }; private prevP: Vec3 = { x: 0, y: 0, z: 0 };
  private mouse = { dx: 0, dy: 0 };
  private shape = new RAPIER.Capsule(0.55, 0.3);

  constructor(private world: RAPIER.World, private blocks: (c: RAPIER.Collider) => boolean) {}

  // out you go: at a ship-frame point, facing a way (a yaw, or a full orientation), moving with a ship-frame velocity
  start(p: Vec3, yawOrM: number | M3, u: Vec3, shipR: M3, shipVel: Vec3) {
    const M = typeof yawOrM === "number" ? rot({ x: 0, y: 1, z: 0 }, yawOrM) : yawOrM;
    this.active = true; this.p = { ...p }; this.prevP = { ...p }; this.u = { ...u };
    this.relP = ap(shipR, p); this.relV = ap(shipR, u); this.R = mul(shipR, M);
    this.prevShipR = shipR.slice(); this.prevShipV = { ...shipVel }; this.q = toQuat(M);
  }
  stop() { this.active = false; }
  mouseMove(dx: number, dy: number) { this.mouse.dx += dx; this.mouse.dy += dy; }
  forward(): Vector3 { return this.dir(0, 0, 1); }
  up(): Vector3 { return this.dir(0, 1, 0); }
  private dir(x: number, y: number, z: number) { const m = new Matrix(); this.q.toRotationMatrix(m); return Vector3.TransformNormal(new Vector3(x, y, z), m); }

  update(dt: number, ship: { R: M3; vel: Vec3 }, keys: Set<string>) {
    if (!this.active) return;
    const R = ship.R, k = (a: string, b: string) => (keys.has(a) ? 1 : 0) - (keys.has(b) ? 1 : 0);
    const thr = k("w", "s"), strafe = k("d", "a"), lift = k(" ", "c"), roll = k("e", "q"), boost = keys.has("shift");
    // what the ship did: changed its velocity, and turned
    const dV = sub(ship.vel, this.prevShipV); this.prevShipV = { ...ship.vel }; this.relV = sub(this.relV, dV);
    const dR = mul(R, tr(this.prevShipR)); this.prevShipR = R.slice();
    const omega = scl({ x: (dR[7] - dR[5]) / 2, y: (dR[2] - dR[6]) / 2, z: (dR[3] - dR[1]) / 2 }, 1 / Math.max(dt, 1e-4));
    // turning: mouse yaw and pitch, Q/E roll, in your own axes (x right, y up, z ahead); the assist turns with the ship
    if (this.assist) this.R = mul(dR, this.R);
    const yawA = this.mouse.dx * 0.0022, pitchA = this.mouse.dy * 0.0022, rollA = -roll * 1.2 * dt; this.mouse.dx = this.mouse.dy = 0;
    this.R = orthonormal(mul(this.R, mul(rot({ x: 0, y: 1, z: 0 }, yawA), mul(rot({ x: 1, y: 0, z: 0 }, pitchA), rot({ x: 0, y: 0, z: 1 }, rollA)))));
    // thrust: the assist holds the velocity you ask for relative to the ship; without it you push and drift
    if (this.assist) {
      const top = boost ? BOOST : SPEED;
      const want = add(ap(this.R, { x: strafe * top * 0.6, y: lift * top * 0.6, z: thr * top }), cross(omega, this.relP));
      const d = sub(want, this.relV), dl = len(d);
      const follow = len(dV) + len(cross(omega, scl(this.relV, dt)));
      const allowed = Math.min(FOLLOW_MAX * dt, follow + (boost ? FEEL * 2 : FEEL) * dt);
      if (dl > 1e-6) this.relV = add(this.relV, scl(d, Math.min(1, allowed / dl)));
      this.saturated = follow > FOLLOW_MAX * dt ? 1.5 : Math.max(0, this.saturated - dt);
    } else this.relV = add(this.relV, ap(this.R, { x: strafe * MANUAL * dt, y: lift * MANUAL * dt, z: thr * MANUAL * (boost ? 2 : 1) * dt }));
    this.relP = add(this.relP, scl(this.relV, dt));
    let p = ap(tr(R), this.relP); const M = mul(tr(R), this.R), q = toQuat(M);
    // never through the hull or a shuttle: cast the body along this frame's move
    const move = sub(p, this.prevP);
    if (len(move) > 1e-5) {
      const hit = this.world.castShape(this.prevP, { x: q.x, y: q.y, z: q.z, w: q.w }, move, this.shape, 0.02, 1, true, undefined, undefined, undefined, undefined, this.blocks);
      if (hit && hit.time_of_impact < 1) { p = add(this.prevP, scl(move, Math.max(0, hit.time_of_impact - 0.02))); this.relP = ap(R, p); this.relV = cross(omega, this.relP); }
    }
    this.prevP = { ...p };
    this.u = ap(tr(R), sub(this.relV, cross(omega, this.relP)));
    this.p = p; this.q = q;
    this.o2 = Math.max(0, this.o2 - dt);
  }
}
